/**
 * Pagination — the table footer: "Showing X–Y of Z", rows-per-page and page buttons.
 *
 * Replaces ~40 inline copies in two drifted families (docs/DUPLICATION_AUDIT.md, B10).
 * The full-size family had SVG chevrons, aria labels and a 5-button window that shifts
 * at the edges; the compact family (profile tabs, dashboard tables) had ‹ › glyphs, no
 * aria labels and a window that shrank at the edges (page 1 showed only 1–3). Both now
 * render the full-size version. The rows-per-page choices are PER_PAGE_OPTIONS, the
 * values usePersistedPerPage accepts.
 *
 * The caller owns all state, so page clamping and "reset to page 1 when per-page
 * changes" stay where they were: pass the same handlers the inline pager used.
 *
 * @param {number}   page            — current page (1-based, already clamped)
 * @param {number}   totalPages
 * @param {number}   total           — row count after filtering
 * @param {number}   perPage
 * @param {function} onPageChange    — called with the new page number
 * @param {function} [onPerPageChange] — called with the new per-page number; omit to hide the select
 * @param {string}   [entityLabel]   — "devices", "events"…; shown after the count and in the nav label
 * @param {string}   [selectId]      — id for the per-page select, so its label is associated
 * @param {string}   [className]     — extra class on the root (page-specific layout tweaks)
 */

import { PER_PAGE_OPTIONS } from '../../hooks/usePersistedPerPage.js';
import './pagination.css';

const MAX_BUTTONS = 5;

/** Up to MAX_BUTTONS page numbers around `page`, shifted at the edges so the window stays full. */
export function getPageWindow(page, totalPages, max = MAX_BUTTONS) {
  const pages = [];
  let start = Math.max(1, page - Math.floor(max / 2));
  const end = Math.min(totalPages, start + max - 1);
  if (end - start < max - 1) start = Math.max(1, end - max + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
}

const Pagination = ({
  page, totalPages, total, perPage,
  onPageChange, onPerPageChange,
  entityLabel, selectId, className,
}) => {
  const first = total > 0 ? (page - 1) * perPage + 1 : 0;
  const last = Math.min(page * perPage, total);
  const pages = getPageWindow(page, totalPages);

  return (
    <div className={`pg-pagination${className ? ` ${className}` : ''}`}>
      <p className="pg-pagination__info">
        Showing <span className="pg-pagination__emphasis">{first}</span>&ndash;<span className="pg-pagination__emphasis">{last}</span> of{' '}
        <span className="pg-pagination__emphasis">{total}</span>{entityLabel ? ` ${entityLabel}` : null}
      </p>
      <div className="pg-pagination__controls">
        {onPerPageChange ? (
          <div className="pg-per-page">
            <label htmlFor={selectId} className="pg-per-page__label">Rows per page:</label>
            <select
              id={selectId}
              value={perPage}
              onChange={(e) => onPerPageChange(parseInt(e.target.value, 10))}
              className="pg-per-page__select"
            >
              {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        ) : null}
        <nav className="pg-page-nav" aria-label={`${entityLabel || 'Table'} pagination`}>
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="pg-page-btn"
            aria-label="Previous page"
          >
            <svg className="pg-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          {pages.map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => onPageChange(num)}
              className={`pg-page-num${num === page ? ' is-active' : ''}`}
              aria-label={`Page ${num}`}
              aria-current={num === page ? 'page' : undefined}
            >
              {num}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="pg-page-btn"
            aria-label="Next page"
          >
            <svg className="pg-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </nav>
      </div>
    </div>
  );
};

export default Pagination;
